import React from 'react';

// Tier 2 / F-20, F-21, F-22 — generic composer-autocomplete state hook.
//
// Drives a Popper-based suggestion list anchored to the composer for
// three distinct triggers:
//
//   '/'  — template quick-insert            (regex: /^\/(\w*)$/)
//   ':'  — emoji shortcode                  (regex: /(?:^|\s):(\w{2,})$/)
//   '@'  — merge-tag autocomplete           (regex: /(?:^|\s)@(\w*)$/)
//
// The hook owns:
//   - active trigger (or null when no popover should show)
//   - the captured filter query (the text after the trigger char)
//   - the highlighted index inside the items list
//
// Callers wire it into the textarea via:
//   onChange  (re-evaluate trigger from the next value + caret pos)
//   onKeyDown (capture Up/Down/Enter/Escape when the popover is open)
//
// Selection is not handled here — the caller passes its own onSelect
// callback into the popover (different per trigger: insert template
// body / insert emoji / insert merge tag token).

const DEFAULT_REGEX_MAP = {
    '/': /^\/(\w*)$/,
    ':': /(?:^|\s):(\w{2,})$/,
    '@': /(?:^|\s)@(\w*)$/,
};

// Find the active trigger and its query given the current text and
// the caret position. We only look at text BEFORE the caret because
// autocomplete should fire on the token the user is currently typing.
const detectTrigger = (text, caret, regexMap) => {
    if (!text) return { trigger: null, query: '', matchStart: 0, matchEnd: 0 };
    const upToCaret = text.slice(0, caret);

    // Iterate map keys in a stable order so '/' is checked first
    // (it's only valid at position 0, while ':' / '@' can appear
    // anywhere after whitespace).
    const keys = Object.keys(regexMap);
    for (let i = 0; i < keys.length; i += 1) {
        const trigger = keys[i];
        const re = regexMap[trigger];
        const m = upToCaret.match(re);
        if (m) {
            // The match includes a leading whitespace for ':' / '@';
            // re-derive the start of the actual token (after the
            // optional leading space) so callers can splice it out.
            const tokenIdx = upToCaret.lastIndexOf(trigger);
            return {
                trigger,
                query: m[1] || '',
                matchStart: tokenIdx,
                matchEnd: caret,
            };
        }
    }
    return { trigger: null, query: '', matchStart: 0, matchEnd: 0 };
};

export default function useComposerAutocomplete({ regexMap = DEFAULT_REGEX_MAP } = {}) {
    const [trigger, setTrigger] = React.useState(null);
    const [query, setQuery] = React.useState('');
    const [matchRange, setMatchRange] = React.useState({ start: 0, end: 0 });
    const [selectedIndex, setSelectedIndex] = React.useState(0);
    // Escape-sticky: once the user dismisses the popover via Escape, suppress
    // re-opening for the same trigger position until the user types a fresh
    // trigger char somewhere else. Without this, evaluate() on the next
    // keystroke (or arrow-key onKeyUp) sees the trigger char is still in the
    // text and immediately re-opens the popover. Tracks the matchStart of
    // the last user-dismissed trigger.
    const dismissedAtRef = React.useRef(null);
    // Track previous query to skip selectedIndex reset on no-op evaluations.
    // Without this, ArrowDown advances index then onKeyUp fires evaluate()
    // with unchanged query and resets index to 0, breaking keyboard nav.
    const prevQueryRef = React.useRef('');
    const prevTriggerRef = React.useRef(null);

    const close = React.useCallback(() => {
        setTrigger(null);
        setQuery('');
        setSelectedIndex(0);
        setMatchRange({ start: 0, end: 0 });
        prevQueryRef.current = '';
        prevTriggerRef.current = null;
    }, []);

    // Re-evaluate state from the textarea's current value + caret.
    // Callers should invoke this from onChange AND onKeyUp (because
    // arrow-key cursor moves don't fire onChange).
    const evaluate = React.useCallback((text, caret) => {
        const detected = detectTrigger(text, caret, regexMap);
        if (!detected.trigger) {
            // Only close if a popover is actually open — avoids
            // spamming setState on every keystroke. Also clear the
            // dismissed-position sentinel: the trigger char is gone,
            // so a future re-typed trigger should open again.
            dismissedAtRef.current = null;
            setTrigger((prev) => (prev ? null : prev));
            setQuery((prev) => (prev ? '' : prev));
            setSelectedIndex((prev) => (prev ? 0 : prev));
            prevQueryRef.current = '';
            prevTriggerRef.current = null;
            return null;
        }
        // Escape-sticky: if the user dismissed at this matchStart, don't
        // re-open. The dismissal clears once the trigger char is removed
        // OR the user moves to a different trigger position.
        if (dismissedAtRef.current === detected.matchStart) {
            return null;
        }
        // User moved to a different trigger (or different position) — clear
        // the dismissal sentinel.
        dismissedAtRef.current = null;
        setTrigger((prev) => (prev === detected.trigger ? prev : detected.trigger));
        setQuery((prev) => (prev === detected.query ? prev : detected.query));
        setMatchRange((prev) => (
            prev.start === detected.matchStart && prev.end === detected.matchEnd
                ? prev
                : { start: detected.matchStart, end: detected.matchEnd }
        ));
        // Only reset highlighted index when the query OR trigger actually
        // changed. Without this guard, every onKeyUp (including arrow keys)
        // clobbers user navigation back to index 0.
        const queryChanged = (
            prevQueryRef.current !== detected.query
            || prevTriggerRef.current !== detected.trigger
        );
        if (queryChanged) {
            setSelectedIndex(0);
        }
        prevQueryRef.current = detected.query;
        prevTriggerRef.current = detected.trigger;
        return detected;
    }, [regexMap]);

    // Build a keydown handler bound to the active items list. The
    // caller passes the current items length + a select callback so
    // Enter can commit the highlighted choice.
    const buildKeyDownHandler = React.useCallback(({ itemCount, onSelect }) => (evt) => {
        if (!trigger) return false; // not handled — caller falls through

        // Escape always handled when popover is open, regardless of itemCount.
        if (evt.key === 'Escape') {
            evt.preventDefault();
            // Mark this position as user-dismissed so evaluate() won't
            // immediately re-open on the next keystroke.
            dismissedAtRef.current = matchRange.start;
            close();
            return true;
        }
        // Enter intercepted whenever popover is open. With 0 items, dismiss
        // the popover instead of letting Enter fall through to the composer's
        // send handler — otherwise a user who types `/xyznonexistent` and hits
        // Enter would accidentally send that literal text.
        if (evt.key === 'Enter' && !evt.shiftKey) {
            evt.preventDefault();
            if (itemCount === 0) {
                dismissedAtRef.current = matchRange.start;
                close();
            } else if (typeof onSelect === 'function') {
                onSelect(selectedIndex);
            }
            return true;
        }
        // Arrow nav only meaningful when there are items.
        if (itemCount === 0) return false;

        if (evt.key === 'ArrowDown') {
            evt.preventDefault();
            setSelectedIndex((i) => (i + 1) % itemCount);
            return true;
        }
        if (evt.key === 'ArrowUp') {
            evt.preventDefault();
            setSelectedIndex((i) => (i - 1 + itemCount) % itemCount);
            return true;
        }
        return false;
    }, [trigger, selectedIndex, matchRange.start, close]);

    return {
        trigger,
        query,
        matchRange,
        selectedIndex,
        setSelectedIndex,
        evaluate,
        close,
        buildKeyDownHandler,
    };
}
